import { SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/*
 * Rich text with a strict style allow-list.
 *
 * KEEP THIS BLOCK IDENTICAL to ALLOWED_STYLES / FORBIDDEN_STYLE_VALUE / filterStyle() in the sc-mobile
 * pipe (src/app/pipes/sanitize-html.pipe.ts). The two clients render the same stored content, so they
 * must keep and drop the same formatting. The API applies a matching CSS allow-list when it stores rich
 * text (HtmlSanitizationService); this list is deliberately a subset of it.
 */

const COLOUR_SOURCE: string = '#[0-9a-f]{3,8}|rgba?\\(\\s*[\\d.]+%?\\s*(,\\s*[\\d.]+%?\\s*){2,3}\\)|[a-z]{3,20}';
// A non-negative length: a bare 0, or a number with a unit. There is no sign in the pattern, so a negative
// value can never match.
const LENGTH_SOURCE: string = '(0|\\d{1,4}(\\.\\d{1,2})?(px|pt|em|rem|%))';
const BORDER_WIDTH_SOURCE: string = '(0|\\d{1,2}(\\.\\d{1,2})?px)';
const BORDER_STYLE_SOURCE: string = '(none|solid|dashed|dotted|double)';

/**
 * Inline style properties that rich text content may keep, with the only value shapes accepted for each.
 * Anything not listed is dropped: position, z-index, display, float, behavior, background (shorthand with
 * url()) and so on. Margins are accepted only when none of the values is negative.
 */
const ALLOWED_STYLES: Map<string, RegExp> = new Map(Object.entries({
  'color': new RegExp(`^(${COLOUR_SOURCE})$`, 'i'),
  'background-color': new RegExp(`^(${COLOUR_SOURCE})$`, 'i'),
  'text-align': /^(left|right|center|justify)$/i,
  'font-size': /^\d{1,3}(\.\d{1,2})?(px|pt|em|rem|%)$/i,
  'font-weight': /^(normal|bold|bolder|lighter|[1-9]00)$/i,
  'font-style': /^(normal|italic|oblique)$/i,
  'text-decoration': /^(none|underline|line-through)( (underline|line-through))?$/i,
  'width': new RegExp(`^(auto|${LENGTH_SOURCE})$`, 'i'),
  'padding': new RegExp(`^${LENGTH_SOURCE}( ${LENGTH_SOURCE}){0,3}$`, 'i'),
  'padding-top': new RegExp(`^${LENGTH_SOURCE}$`, 'i'),
  'padding-right': new RegExp(`^${LENGTH_SOURCE}$`, 'i'),
  'padding-bottom': new RegExp(`^${LENGTH_SOURCE}$`, 'i'),
  'padding-left': new RegExp(`^${LENGTH_SOURCE}$`, 'i'),
  'margin': new RegExp(`^(auto|${LENGTH_SOURCE})( (auto|${LENGTH_SOURCE})){0,3}$`, 'i'),
  'margin-top': new RegExp(`^(auto|${LENGTH_SOURCE})$`, 'i'),
  'margin-right': new RegExp(`^(auto|${LENGTH_SOURCE})$`, 'i'),
  'margin-bottom': new RegExp(`^(auto|${LENGTH_SOURCE})$`, 'i'),
  'margin-left': new RegExp(`^(auto|${LENGTH_SOURCE})$`, 'i'),
  'border': new RegExp(`^${BORDER_WIDTH_SOURCE}( ${BORDER_STYLE_SOURCE})?( (${COLOUR_SOURCE}))?$`, 'i'),
  'border-width': new RegExp(`^${BORDER_WIDTH_SOURCE}( ${BORDER_WIDTH_SOURCE}){0,3}$`, 'i'),
  'border-style': new RegExp(`^${BORDER_STYLE_SOURCE}$`, 'i'),
  'border-color': new RegExp(`^(${COLOUR_SOURCE})$`, 'i'),
  'border-collapse': /^(collapse|separate)$/i,
}));

/**
 * Anything here rejects the declaration whatever the property, as a second line of defence behind the
 * per-property patterns: url(), expression(), javascript: and data: values, behavior, escapes, comments
 * and markup characters.
 */
const FORBIDDEN_STYLE_VALUE: RegExp = /url\s*\(|expression|javascript:|data:|behaviou?r|\\|\/\*|[<>@]/i;

/**
 * Keep only allow-listed style properties whose values match their strict pattern.
 */
function filterStyle(style: string): string {
  const kept: string[] = [];
  style.split(';').forEach((declaration: string): void => {
    const separator: number = declaration.indexOf(':');
    if (separator < 0) {
      return;
    }
    const property: string = declaration.slice(0, separator).trim().toLowerCase();
    const value: string = declaration.slice(separator + 1).trim();
    const pattern: RegExp | undefined = ALLOWED_STYLES.get(property);
    if (pattern && !FORBIDDEN_STYLE_VALUE.test(value) && pattern.test(value)) {
      kept.push(`${property}: ${value}`);
    }
  });
  return kept.join('; ');
}

/**
 * Random lowercase hex string used to make marker classes unguessable.
 */
function makeNonce(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte: number): string => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Sanitises server supplied rich text and keeps its allow-listed inline styles.
 *
 * The body is run through Angular's own HTML sanitiser, which removes scripts, event handlers, iframes and
 * every inline style. Styles are put back afterwards from values this function validated itself, never from
 * the supplied markup:
 *  1. every `style` attribute is replaced by an unguessable marker class (invalid declarations are dropped);
 *  2. Angular's sanitiser (SecurityContext.HTML) runs over the result;
 *  3. each marker class is turned back into a style attribute holding only the validated declarations.
 *
 * Iframes are removed here. Induction video is handled by SafeRichContentComponent, which rebuilds allow
 * listed embeds before it calls this.
 *
 * The returned string is Angular's sanitiser output plus style values that matched the patterns above, so
 * it is marked trusted. Raw input is never trusted: without the marking Angular would sanitise the string a
 * second time and strip the styles again.
 */
export function sanitizeHtmlKeepingStyles(sanitizer: DomSanitizer, value: unknown): SafeHtml {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // The parsed document is inert: nothing in it runs or loads.
  const doc: Document = new DOMParser().parseFromString(String(value), 'text/html');
  const nonce: string = makeNonce();

  doc.body.querySelectorAll('iframe').forEach((iframe: Element): void => iframe.remove());

  const styles: string[] = [];
  doc.body.querySelectorAll('[style]').forEach((element: Element): void => {
    const safeStyle: string = filterStyle(element.getAttribute('style') || '');
    if (safeStyle) {
      styles.push(safeStyle);
      element.classList.add(`sc${nonce}s${styles.length - 1}`);
    }
  });

  const sanitized: string = sanitizer.sanitize(SecurityContext.HTML, doc.body.innerHTML) || '';

  const output: Document = new DOMParser().parseFromString(sanitized, 'text/html');
  const markerPattern: RegExp = new RegExp(`^sc${nonce}s(\\d+)$`);
  output.body.querySelectorAll('[class]').forEach((element: Element): void => {
    Array.from(element.classList).forEach((className: string): void => {
      const match: RegExpMatchArray | null = className.match(markerPattern);
      if (match) {
        element.classList.remove(className);
        if (styles[Number(match[1])] !== undefined) {
          element.setAttribute('style', styles[Number(match[1])]);
        }
      }
    });
    if (element.classList.length === 0) {
      element.removeAttribute('class');
    }
  });

  return sanitizer.bypassSecurityTrustHtml(output.body.innerHTML);
}
